<template>
    <div class="col-12 mx-auto">
        <Card>
            <template #title>
                <h5>{{ dialogHeader }}</h5>
            </template>

            <template #content>

                <Accordion :activeIndex="0">
                    <AccordionTab :header="`Author and ${label('Debrief')} Data`">
                        <div class="p-fluid formgrid grid">
                            <div class="col-2">
                                Name:
                            </div>
                            <div class="col-4">
                                <b>{{ authorName }}</b>
                            </div>
                            <div class="col-2">
                                Surname:
                            </div>
                            <div class="col-4">
                                <b>{{ authorSurname }}</b>
                            </div>
                            <div class="col-2">
                                Email:
                            </div>
                            <div class="col-10">
                                <b>{{ authorEmail }}</b>
                            </div>
                            <div class="col-12">
                                <hr />
                            </div>
                            <div class="col-2">
                                {{ label('Debrief') }} creation date:
                            </div>
                            <div class="col-10">
                                {{ debriefCreationDate }}
                            </div>
                        </div>
                    </AccordionTab>
                </Accordion>

                <Accordion :activeIndex="0">
                    <AccordionTab header="Incident Exercise Training">
                        <div class="p-fluid formgrid grid">
                            <IncidentForm />
                            <Divider />
                            <div class="field col-12 md:col-4">
                                <base-dropdown v-model="debrief.appliance_id" label="CallSign" :options="appliances" :errorMessage="formErrors.applianceId" :disabled="editMode" />
                            </div>
                            <Divider />
                            <template v-if="authStore.hasPermission(UserPermission.WRITE_LEARNING_OUTCOME)">
                                <div v-if="!editMode" class="field-radiobutton col-12 justify-content-center">
                                    <base-selection-input v-model="debrief.learning" label="Was Learning Identified? " :radio-type="true" :options="getYesNoOptions()" customStyle="flex-row" />
                                </div>
                                <div v-else class="mx-auto font-bold">
                                    <span v-if="learningOutcomes.length > 0">Learning Identified</span>
                                    <span v-else-if="learningOutcomes.length <= 0 && learningIdentified">Learning outcomes not yet created</span>
                                    <span v-else>Learning not identified</span>
                                </div>
                            </template>
                        </div>
                    </AccordionTab>
                </Accordion>
                <learning-outcomes-form v-if="learningIdentified" ref="LOComponent"
                                        @fileUploadingFinished="onFileUploadingFinished"
                                        @fileUploadingStarted="onFileUploadingStarted"/>
            </template>
            <template #footer>
                <div class="text-center">
                    <Button :label="editMode ? 'Update' : 'Submit'" class="mt-2" :class="[editMode ? 'p-button-primary' : 'p-button-sucess']" :disabled="saving" @click="submit" />
                </div>
            </template>
        </Card>
    </div>


    <div class="col-12">
        <Accordion>
            <AccordionTab v-if="entityLogIds.length > 0" header="Events">
                <LogViewer :event_type="logEventTypes" :entity_ids="entityLogIds"/>
            </AccordionTab>
        </Accordion>
    </div>
</template>

<script setup>
import LearningOutcomesForm from '../../learning-outcome/modules/LearningOutcomesForm.vue';
import IncidentForm from '../../incident/modules/IncidentForm.vue';

import { storeToRefs } from 'pinia';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { authStore, applianceStore, debriefStore, formErrorStore, incidentStore, learningOutcomeStore } from '../../../../../store/global-stores';
import { handleValidationErrors } from '../../../../../utilities/form-errors-handler';
import { showToast } from '../../../../../utilities/toast-handler';
import { getYesNoOptions } from '../../../../../constants';
import { UserPermission } from '../../../../../modules/permission/enum';
import { DebriefSchema } from '../../../../../utilities/schemas';
import { formatDisplayDate } from '@/utilities/date-formatter';
import LogViewer from '@/product/ams/components/LogViewer.vue';
import { useLabels } from '@/composables/LabelComposable';

const router = useRouter();
const route = useRoute();
const { label } = useLabels();
const { appliances } = storeToRefs(applianceStore);
const { debrief, editId } = storeToRefs(debriefStore);
const { incident, incidents } = storeToRefs(incidentStore);
const { learningOutcomes } = storeToRefs(learningOutcomeStore);
const { formErrors } = storeToRefs(formErrorStore);

const LOComponent = ref(null);
const debriefId = computed(() => {
    return route.query.debriefId ? parseInt(route.query.debriefId) : null;
});

const authorName = ref('');
const authorSurname = ref('');
const authorEmail = ref('');
const debriefCreationDate = ref('');

const haveToWaitForFiles = ref(false);

const entityLogIds = ref([])
const loading = ref(false)

const logEventTypes = [
    'debrief',
]

onMounted(() => {
    debriefStore.initDebrief();
    learningOutcomeStore.initLearningOutcomes();

    entityLogIds.value = []
    if (typeof debriefId.value !== 'undefined') {
        loading.value = true
        entityLogIds.value.push(debriefId.value)
    }

    applianceStore.fetchAllAppliances();
    // check if there is debriefId
    // if yes then its edit mode, so you will have to load details and load them into state
    // if no continue as it is
    if (debriefId.value) {
        editId.value = debriefId.value;
        loadEditDetails(debriefId.value);
    }
});

onUnmounted(() => {
    debriefStore.initDebrief();
    learningOutcomeStore.initLearningOutcomes();
});

function onFileUploadingStarted() {
    haveToWaitForFiles.value = true;
}

function onFileUploadingFinished() {
    haveToWaitForFiles.value = false;
}

const loadEditDetails = async (id) => {
    const debriefDetails = await debriefStore.fetchDebriefDetails(id);
    // load incident details
    incidentStore.loadIncidentDetails(debriefDetails.incident);
    // load debrief details
    debriefStore.loadEditDebrief(debriefDetails);
    // load learning outcome details
    learningOutcomeStore.loadEditLearningOutcomes(debriefDetails.learning_outcome);

    if (debriefDetails.author) {
        authorName.value = debriefDetails?.author.name;
        authorSurname.value = debriefDetails?.author.surname;
        authorEmail.value = debriefDetails?.author.email;

        debriefCreationDate.value = formatDisplayDate(debriefDetails?.created_at);
    }
};

const editMode = computed(() => {
    return editId.value ? true : false;
});

const dialogHeader = computed(() => {
    return editMode.value ? `Edit ${label('Debrief')}` : `Add ${label('Debrief')}`;
});

const learningIdentified = computed(() => {
    return debrief.value.learning;
});

const saving = ref(false);

function waitFor(condition, callback) {
    if(!condition()) {
        //console.log('waiting');
        window.setTimeout(waitFor.bind(null, condition, callback), 100); /* this checks the flag every 100 milliseconds*/
    } else {
        //console.log('done');
        callback();
    }
}

const navigateAfterDebriefSave = () => {
    // Submit-only users (no read_debrief) cannot open the debrief list
    if (authStore.hasPermission(UserPermission.READ_DEBRIEF)) {
        return router.push({ name: 'Debriefs' });
    }
    return router.push({ name: 'Home' });
};

const submit = async () => {
    saving.value = true;
    try {
        if (!debrief.value.learning) {
            await DebriefSchema.validate({ ...incident.value, ...debrief.value }, { abortEarly: false });
        } else {
            await DebriefSchema.validate({ ...incident.value, ...debrief.value, ...{ learningOutcomes: learningOutcomes.value } }, { abortEarly: false });
        }
        formErrorStore.clearErrors();
        
        // If its not edit mode then check if incident number exists
        if (!editMode.value) debrief.value.incident_id = await createIncidentCase();

        if (!editMode.value) {
            createDebriefAndLearningOutcome().then(async () => {
                //await new Promise(resolve => setTimeout(resolve, 10000));

                waitFor(() => haveToWaitForFiles.value === false, () => {
                    navigateAfterDebriefSave().then(() => {
                        saving.value = false;
                    });
                });
            });
        } else {
            updateDebriefAndLearningOutcome().then(async () => {
                //await new Promise(resolve => setTimeout(resolve, 10000));

                waitFor(() => haveToWaitForFiles.value === false, () => {
                    navigateAfterDebriefSave().then(() => {
                        saving.value = false;
                    });
                });
            });
        }
    } catch (error) {
        saving.value = false;
        if (error.inner && typeof error.inner === 'object') {
            const errors = handleValidationErrors(error);
            formErrorStore.addErrors(errors);
            showToast({}, 'Input data invalid');
        }
    }
};

// Returns the incident id if it exists, otherwise creates a new one and returns the id
const createIncidentCase = async () => {
    if (incident.value.incident_number) {
        // check if incident number already exists
        await incidentStore.fetchIncidentList(incident.value.incident_number);
        if (incidents.value && incidents.value.length > 0) {
            const existingIncident = incidents.value.find((obj) => obj.incident_number === incident.value.incident_number);
            // Check if incident number matches on the existing list
            if (existingIncident) {
                return existingIncident.id;
            }
        }
    }

    // If it doesnt exist then create a new one
    return await incidentStore.createIncident();
};

const createDebriefAndLearningOutcome = async () => {
    const debrief_id = await debriefStore.createDebrief();
    if (!debrief.value.learning && debrief_id) {
        navigateAfterDebriefSave();
    }

    if (LOComponent.value) {
        LOComponent.value.saveLearningOutcomes(debrief_id);
    }
};

const updateDebriefAndLearningOutcome = async () => {
    if (LOComponent.value) {
        LOComponent.value.saveLearningOutcomes(editId.value);
    }
};
</script>
